<script setup>
const props = defineProps({
  idSuffix: { default: '', type: String },
})

const { t, locale } = useI18n()

const today   = new Date().toISOString().split('T')[0]
const plus3   = new Date(Date.now() + 3 * 86_400_000).toISOString().split('T')[0]
const checkIn  = ref(today)
const checkOut = ref(plus3)
const adults    = ref(2)
const children  = ref(0)
const childAges = ref([])
const rooms     = ref(1)

watch(checkIn, (val) => {
  if (val && checkOut.value && val >= checkOut.value) {
    checkOut.value = new Date(new Date(val).getTime() + 86_400_000).toISOString().split('T')[0]
  }
})

watch(children, (val) => {
  const n = Math.max(0, Math.min(6, Number(val)))
  const current = childAges.value.slice(0, n)
  while (current.length < n) current.push(null)
  childAges.value = current
})

const ageErrors = ref(false)

// Smooth height grow/shrink for the child-ages row appearing/disappearing
// (CSS alone can't transition to/from height:auto), so the CTA below it
// eases into its new position instead of jumping.
const onChildAgesEnter = (el) => {
  el.style.height = '0px'
  el.style.overflow = 'hidden'
  requestAnimationFrame(() => {
    el.style.transition = 'height 250ms ease'
    el.style.height = `${el.scrollHeight}px`
  })
}
const onChildAgesAfterEnter = (el) => {
  el.style.height = ''
  el.style.overflow = ''
  el.style.transition = ''
}
const onChildAgesLeave = (el) => {
  el.style.height = `${el.scrollHeight}px`
  el.style.overflow = 'hidden'
  requestAnimationFrame(() => {
    el.style.transition = 'height 250ms ease'
    el.style.height = '0px'
  })
}
const onChildAgesAfterLeave = (el) => {
  el.style.height = ''
  el.style.overflow = ''
  el.style.transition = ''
}

const gridCols = computed(() => {
  const n = Number(children.value)
  if (n <= 3) return Math.max(1, n)
  return Math.ceil(n / 2)
})
const gridColsDesktop = computed(() => Math.min(Number(children.value), 8))

const dayCount = computed(() => {
  if (!checkIn.value || !checkOut.value) return 0
  const diff = new Date(checkOut.value).getTime() - new Date(checkIn.value).getTime()
  return Math.max(0, Math.ceil(diff / (1000 * 3600 * 24)))
})

// "01 Eyl" / "01 Sep" — short, locale-aware date display for the
// custom-styled date fields (the real <input type="date"> stays
// functional underneath, this is just what's shown on top of it).
const formatDate = (isoStr) => {
  if (!isoStr) return ''
  const d = new Date(`${isoStr}T00:00:00`)
  const intlLocale = locale.value === 'en' ? 'en-GB' : 'tr-TR'
  return new Intl.DateTimeFormat(intlLocale, { day: '2-digit', month: 'short' }).format(d)
}

const openReservation = () => {
  if (childAges.value.some(a => a === null)) {
    ageErrors.value = true
    return
  }
  ageErrors.value = false
  if (!checkIn.value || !checkOut.value) {
    window.open('https://salamis-bay-conti-hotel.hotelrunner.com/bv3/search', '_blank')
    return
  }
  const c = Number(children.value)
  const ages = childAges.value.map(Number)
  const roomCount = Number(rooms.value)
  const adultCount = Number(adults.value)
  const base = Math.floor(adultCount / roomCount)
  const extra = adultCount % roomCount
  const roomsArr = Array.from({ length: roomCount }, (_, i) => {
    const ra = base + (i < extra ? 1 : 0)
    const rc = i === 0 ? c : 0
    return { adult_count: ra, guest_count: ra + rc, child_count: rc, child_ages: i === 0 ? ages : [] }
  })
  const params = {
    checkin_date: checkIn.value,
    checkout_date: checkOut.value,
    day_count: dayCount.value,
    room_count: roomCount,
    total_adult: adultCount,
    total_child: c,
    rooms: roomsArr,
    guest_rooms: Object.fromEntries(roomsArr.map((r, i) => [String(i), r]))
  }
  window.open(
    `https://salamis-bay-conti-hotel.hotelrunner.com/bv3/search?search=${encodeURIComponent(JSON.stringify(params))}`,
    '_blank'
  )
}
</script>

<template>
  <div class="rb-wrap">
    <form class="rb-card" @submit.prevent="openReservation">
      <div class="rb-dates">
        <div class="rb-date-field">
          <label class="rb-date-label" :for="`rb-checkin${props.idSuffix}`">{{ $t('check_in') }}</label>
          <span class="rb-date-value">
            {{ formatDate(checkIn) }}
            <svg class="rb-date-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="4" rx="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg>
          </span>
          <input
            :id="`rb-checkin${props.idSuffix}`"
            class="rb-date-input"
            type="date"
            v-model="checkIn"
            :min="today"
            required
          />
        </div>
        <span class="rb-divider"></span>
        <div class="rb-date-field">
          <label class="rb-date-label" :for="`rb-checkout${props.idSuffix}`">{{ $t('check_out') }}</label>
          <span class="rb-date-value">
            {{ formatDate(checkOut) }}
            <svg class="rb-date-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="4" rx="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg>
          </span>
          <input
            :id="`rb-checkout${props.idSuffix}`"
            class="rb-date-input"
            type="date"
            v-model="checkOut"
            :min="checkIn || today"
            required
          />
        </div>
      </div>

      <div class="rb-counters">
        <div class="rb-counter-field">
          <span class="rb-counter-label">{{ $t('adults') }}</span>
          <div class="rb-counter">
            <button type="button" class="rb-counter-btn" @click="adults = Math.max(1, adults - 1)" :aria-label="$t('adults') + ' -'">−</button>
            <span class="rb-counter-val">{{ adults }}</span>
            <button type="button" class="rb-counter-btn" @click="adults = Math.min(9, adults + 1)" :aria-label="$t('adults') + ' +'">+</button>
          </div>
        </div>
        <span class="rb-divider"></span>
        <div class="rb-counter-field">
          <span class="rb-counter-label">{{ $t('children') }}</span>
          <div class="rb-counter">
            <button type="button" class="rb-counter-btn" @click="children = Math.max(0, children - 1)" :aria-label="$t('children') + ' -'">−</button>
            <span class="rb-counter-val">{{ children }}</span>
            <button type="button" class="rb-counter-btn" @click="children = Math.min(6, children + 1)" :aria-label="$t('children') + ' +'">+</button>
          </div>
        </div>
        <span class="rb-divider"></span>
        <div class="rb-counter-field">
          <span class="rb-counter-label">{{ $t('room_num') }}</span>
          <div class="rb-counter">
            <button type="button" class="rb-counter-btn" @click="rooms = Math.max(1, rooms - 1)" :aria-label="$t('room_num') + ' -'">−</button>
            <span class="rb-counter-val">{{ rooms }}</span>
            <button type="button" class="rb-counter-btn" @click="rooms = Math.min(9, rooms + 1)" :aria-label="$t('room_num') + ' +'">+</button>
          </div>
        </div>
      </div>

      <Transition
        @enter="onChildAgesEnter"
        @after-enter="onChildAgesAfterEnter"
        @leave="onChildAgesLeave"
        @after-leave="onChildAgesAfterLeave"
      >
        <div v-if="children > 0" class="rb-child-ages">
          <span v-if="ageErrors" class="rb-cta-error">{{ $t('child_age_required') }}</span>
          <div class="rb-child-ages-row" :style="{ '--child-cols': gridCols, '--child-cols-desktop': gridColsDesktop }">
            <div v-for="(_, i) in childAges" :key="i" class="rb-child-age-wrap">
              <label class="rb-child-age-label" :class="{ 'age-error': ageErrors && childAges[i] === null }">{{ $t('child_age_label') }} {{ i + 1 }}</label>
              <select class="rb-child-age-select" v-model="childAges[i]" :class="{ 'age-error': ageErrors && childAges[i] === null }">
                <option :value="null" disabled>–</option>
                <option v-for="a in 12" :key="a - 1" :value="a - 1">{{ a - 1 }}</option>
              </select>
            </div>
          </div>
        </div>
      </Transition>

      <button type="submit" class="rb-cta">{{ $t('book_now') }}</button>
    </form>
  </div>
</template>

<style scoped>
.rb-wrap {
  --navy-900: #0B1E2D;
  --navy-800: #122B3F;
  --gold-400: #E1C887;
  --gold-700: #A87C2E;
  --sand-50: #FAF5E9;
  --glass-fill: #E7BB7E;
  --text-muted: rgba(11,30,45,.55);
  --border-soft: rgba(11,30,45,.14);
  --font-serif: 'Cormorant Garamond', Georgia, serif;
  --font-sans: 'Cormorant Garamond', Georgia, serif;
  --fw-medium: 500;
  --radius-md: 8px;
  --shadow-card: 0 16px 40px rgba(7,21,31,.4);
  --dur-base: 280ms;
  --ease-out: cubic-bezier(.22,.61,.36,1);

  position: relative;
  z-index: 10;
  margin: 0 auto;
  max-width: 1200px;
  width: 100%;
}

/* ─── Card (mobile/tablet default) ────────────────────────── */
.rb-card {
  background: var(--glass-fill);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  overflow: hidden;
}

.rb-divider { background: var(--border-soft); }

/* ─── Dates row ────────────────────────────────────────────── */
.rb-dates {
  display: grid;
  grid-template-columns: 1fr 1px 1fr;
  align-items: stretch;
}
.rb-date-field {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
  padding: 24px;
}
.rb-date-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .18em;
  text-transform: uppercase;
  color: var(--navy-900);
}
.rb-date-value {
  display: flex;
  align-items: center;
  gap: 24px;
  font-family: var(--font-serif);
  font-size: 21px;
  color: var(--navy-900);
  line-height: 1;
}
.rb-date-icon {
  flex-shrink: 0;
  margin-left: 25px;
  color: var(--navy-900);
}
.rb-date-input {
  position: absolute;
  inset: 0;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  opacity: 0;
  cursor: pointer;
}

/* ─── Counters row ─────────────────────────────────────────── */
.rb-counters {
  display: grid;
  grid-template-columns: 1fr 1px 1fr 1px 1fr;
  align-items: center;
  border-top: 1px solid var(--border-soft);
}
.rb-counter-field {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 4px;
}
.rb-counter-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .16em;
  text-transform: uppercase;
  color: var(--navy-900);
}
.rb-counter {
  display: flex;
  align-items: center;
  gap: 2px;
}
.rb-counter-val {
  font-family: var(--font-serif);
  font-size: 18px;
  color: var(--navy-900);
  min-width: 14px;
  text-align: center;
}
.rb-counter-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--gold-700);
  font-size: 17px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: color 150ms;
}
.rb-counter-btn:hover { color: var(--navy-900); }

/* ─── Child ages ───────────────────────────────────────────── */
.rb-child-ages {
  padding: 24px;
  border-top: 1px solid var(--border-soft);
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.rb-child-ages-row {
  display: grid;
  grid-template-columns: repeat(var(--child-cols, 3), 1fr);
  gap: 10px 14px;
}
.rb-child-age-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.rb-child-age-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .16em;
  text-transform: uppercase;
  color: var(--navy-900);
  transition: color 160ms;
}
.rb-child-age-label.age-error { color: #b3261e; }
.rb-child-age-select {
  appearance: none;
  -webkit-appearance: none;
  background: none;
  border: none;
  border-bottom: 1px solid var(--border-soft);
  border-radius: 0;
  padding: 2px 0;
  color: var(--navy-900);
  font-family: var(--font-serif);
  font-size: 18px;
  cursor: pointer;
  min-width: 50px;
  outline: none;
}
.rb-child-age-select:focus,
.rb-child-age-select:focus-visible {
  outline: none;
  border-bottom-color: var(--navy-900);
}
.rb-child-age-select option { background: #fff; color: var(--navy-900); }
.rb-child-age-select.age-error { border-bottom-color: #b3261e !important; color: #b3261e; }
.rb-cta-error {
  font-size: 10px;
  font-weight: 700;
  color: #b3261e;
  letter-spacing: .06em;
}

/* ─── CTA ──────────────────────────────────────────────────── */
.rb-cta {
  display: block;
  width: 100%;
  height: 64px;
  border: none;
  background: var(--navy-900);
  color: var(--gold-400);
  font-family: var(--font-sans);
  font-weight: var(--fw-medium);
  font-size: 12px;
  letter-spacing: .2em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background var(--dur-base) var(--ease-out);
}
.rb-cta:hover { background: var(--navy-800); }

/* ─── Desktop: same design language, collapsed onto one line ─── */
@media (min-width: 1101px) {
  .rb-wrap { width: calc(100% - 8%); }

  .rb-card {
    position: relative;
    display: grid;
    grid-template-columns: auto auto auto;
    width: fit-content;
    margin-inline: auto;
  }

  /* child-ages spans the full width as its own row, above the main row —
     same card, same background, no separate panel/shadow/gap. Since the
     whole module is anchored to the page by its bottom edge, this row
     growing/shrinking extends the card upward, never down into the
     section below. */
  .rb-child-ages {
    grid-row: 1;
    grid-column: 1 / -1;
    border-top: none;
    border-bottom: 1px solid var(--border-soft);
  }
  .rb-child-ages-row { grid-template-columns: repeat(var(--child-cols-desktop, 8), 1fr); }

  .rb-dates { grid-row: 2; grid-column: 1; grid-template-columns: auto 1px auto; }
  .rb-date-field { padding: 10px 28px; }
  .rb-date-value { font-size: 14px; }

  .rb-counters {
    grid-row: 2; grid-column: 2;
    grid-template-columns: auto 1px auto 1px auto;
    border-top: none;
    border-left: 1px solid var(--border-soft);
  }
  .rb-counter-field { padding: 8px 24px; }

  .rb-cta {
    grid-row: 2; grid-column: 3;
    width: auto;
    height: auto;
    padding: 0 40px;
    border-left: 1px solid var(--border-soft);
    border-radius: 0 var(--radius-md) var(--radius-md) 0;
  }
}
</style>
